// Custo ESTIMADO da IA: tokens informados pelo provedor × preço de lista (US$ por milhão).
// O valor cobrado de verdade só o painel de cobrança de cada provedor tem.

export type OrigemUso = 'leitura' | 'chat' | 'resumo'
export type UsoIa = { provedor: string; modelo: string; origem: OrigemUso; chamadas: number; tokensEntrada: number; tokensSaida: number }
export type PrecoIa = { provedor: string; modelo: string; entrada: number; saida: number; fonte: string | null; conferidoEm: string | null }

export const NOME_ORIGEM: Record<OrigemUso, string> = {
  leitura: 'Leituras e análises dos painéis',
  chat: 'Pergunte à IA',
  resumo: 'Resumos e relatórios',
}

export const custoDe = (u: Pick<UsoIa, 'tokensEntrada' | 'tokensSaida'>, p: Pick<PrecoIa, 'entrada' | 'saida'> | undefined): number | null =>
  p ? (u.tokensEntrada * p.entrada + u.tokensSaida * p.saida) / 1_000_000 : null

export type LinhaModelo = { modelo: string; chamadas: number; tokensEntrada: number; tokensSaida: number; preco: PrecoIa | undefined; custo: number | null }
export type ResumoUso = {
  chamadas: number
  tokensEntrada: number
  tokensSaida: number
  /** Soma só do que tem preço; os modelos sem preço ficam em `semPreco`. */
  custo: number
  semPreco: string[]
  porModelo: LinhaModelo[]
  porOrigem: Array<{ origem: OrigemUso; chamadas: number; custo: number }>
}

/**
 * O uso de um provedor no período. Entram como linha os modelos usados, os com preço cadastrado
 * e os `extras` (o modelo configurado), para dar onde editar o preço mesmo antes do primeiro uso.
 */
export function resumirUso(uso: UsoIa[], precos: PrecoIa[], provedor: string, extras: string[] = []): ResumoUso {
  const doProvedor = uso.filter((u) => u.provedor === provedor)
  const precoDe = (modelo: string) => precos.find((p) => p.provedor === provedor && p.modelo === modelo)
  const modelos = [...new Set([...doProvedor.map((u) => u.modelo), ...precos.filter((p) => p.provedor === provedor).map((p) => p.modelo), ...extras.filter(Boolean)])]

  const porModelo = modelos
    .map((modelo) => {
      const us = doProvedor.filter((u) => u.modelo === modelo)
      const soma = { chamadas: 0, tokensEntrada: 0, tokensSaida: 0 }
      for (const u of us) {
        soma.chamadas += u.chamadas
        soma.tokensEntrada += u.tokensEntrada
        soma.tokensSaida += u.tokensSaida
      }
      const preco = precoDe(modelo)
      return { modelo, ...soma, preco, custo: soma.chamadas ? custoDe(soma, preco) : 0 }
    })
    // Usados primeiro (do mais caro), depois os só com preço, em ordem alfabética.
    .sort((a, b) => b.chamadas - a.chamadas || (b.custo ?? 0) - (a.custo ?? 0) || a.modelo.localeCompare(b.modelo))

  const origens: OrigemUso[] = ['leitura', 'chat', 'resumo']
  const porOrigem = origens
    .map((origem) => {
      const us = doProvedor.filter((u) => u.origem === origem)
      return { origem, chamadas: us.reduce((s, u) => s + u.chamadas, 0), custo: us.reduce((s, u) => s + (custoDe(u, precoDe(u.modelo)) ?? 0), 0) }
    })
    .filter((o) => o.chamadas > 0)

  return {
    chamadas: porModelo.reduce((s, m) => s + m.chamadas, 0),
    tokensEntrada: porModelo.reduce((s, m) => s + m.tokensEntrada, 0),
    tokensSaida: porModelo.reduce((s, m) => s + m.tokensSaida, 0),
    custo: porModelo.reduce((s, m) => s + (m.custo ?? 0), 0),
    semPreco: porModelo.filter((m) => m.chamadas > 0 && !m.preco).map((m) => m.modelo),
    porModelo,
    porOrigem,
  }
}

const usd = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const usdPreco = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 })

/** "US$ 1,23"; abaixo de um centavo, "< US$ 0,01" (não "US$ 0,00", que parece de graça). */
export const formatarDolar = (v: number) => (v > 0 && v < 0.005 ? `< ${usd.format(0.01)}` : usd.format(v))
export const formatarPreco = (v: number) => usdPreco.format(v)
